/**
 * @fileoverview Configuration for bespoke background images.
 * This file centralizes the logic for brand-specific background image overrides,
 * replacing previous global CSS rules that relied on `data-brand` attributes.
 *
 * Each configuration object (e.g., `bespokeUpperConfig`) maps brand prefixes
 * to their specific background image (`bgSrc`) and additional inline styles
 * (`opacity`, `backgroundPosition`). It also handles nested conditions like
 * `grs`, `treatment`, `email`, or `math` flags.
 *
 * To configure a new Bespoke brand:
 * 1.  Identify the `brand` prefix (e.g., '2000').
 * 2.  Determine which section(s) require bespoke backgrounds: `upper`, `lowerLeft`, or `lowerRight`.
 * 3.  Add a new entry to the corresponding configuration object (`bespokeUpperConfig`, `bespokeLeftConfig`, `bespokeRightConfig`).
 * 4.  Define the `bgSrc` (background image URL) and any other inline styles.
 * 5.  If there are additional conditions (e.g., `grs`, `treatment`), nest them appropriately.
 *
 *    Example Structure:
 *    '<BRAND_PREFIX>': {
 *      bgSrc: 'url("path/to/default/image.jpg")', // Simple brand-specific image
 *      opacity: 0.8, // Optional: Additional inline styles
 *      backgroundPosition: 'center', // Optional
 *
 *      // Nested conditions (example for '0110' with 'grs')
 *      default: { bgSrc: 'url("path/to/default/image.jpg")' }, // Required if nested conditions exist
 *      grs: { bgSrc: 'url("path/to/grs/image.jpg")' },
 *
 *      // Nested conditions (example for '1030' with 'treatment')
 *      treatment: {
 *        ExpeDRY: { bgSrc: 'url("path/to/expedry/image.jpg")' }
 *      },
 *
 *      // Nested conditions (example for '0324' with 'email')
 *      email: {
 *        'SPECIFIC_EMAIL_FLAG_1': { bgSrc: 'url("path/to/email1/image.jpg")' },
 *        'SPECIFIC_EMAIL_FLAG_2': { bgSrc: 'url("path/to/email2/image.jpg")' },
 *      },
 *
 *      // Nested conditions (example for '1427' with 'math')
 *      math: {
 *        'a': { bgSrc: 'url("path/to/mathA/image.jpg")' },
 *        'b': { bgSrc: 'url("path/to/mathB/image.jpg")' },
 *      }
 *    }
 *
 * REMEMBER:
 * - After updating this configuration, the changes will be automatically applied 
 *   to DetailPanel.js via the getBespokeConfig utility.
 * - CRUCIALLY, remove the corresponding `data-brand` (and `data-treatment`, etc.)
 *   CSS rules from `App.css` to prevent conflicts and keep the stylesheet clean.
 */

const bespokeUpperConfig = {
  '0110': {
    default: { bgSrc: 'url("/img/0110/aside-ATA-Header-Clean.png")' },
    grs: { bgSrc: 'url("/img/aside-one-grs.jpg")' },
  },
  '0116': { bgSrc: 'url("/img/0116/afd-DISPLACE-2.jpg")', opacity: 0.85 },
  '2206': { bgSrc: 'url("/img/2206/itc-scorched.jpg")', backgroundPosition: 'top center' },
  '1001': { bgSrc: 'url("/img/1001/1001-upper.jpg")' },
  '1029': { bgSrc: 'url("/img/1029/1001-upper.jpg")' },
  '1027': { bgSrc: 'url("/img/1027/1027-upper.jpg")' },
  '1028': { bgSrc: 'url("/img/1028/1028-upper-ski.jpg")', opacity: 0.75 },
  '1030': {
    default: { bgSrc: 'url("/img/1028/1028-upper-ski.jpg")' }, // Fallback from 1028
    treatment: {
      ExpeDRY: { bgSrc: 'url("/img/1030/1030-upper-discovery.jpg")' }
    }
  },
  '1107': { bgSrc: 'url("/img/1107/aside-one.jpg")' },
  '0324': {
    email: {
      '0324L23FN2790': { bgSrc: 'url("/img/0324/0324-upper.jpg")' },
      '03240315388': { bgSrc: 'url("/img/0324/0324-upper-expedry.jpg")' },
    }
  },
  '0805': { bgSrc: 'url("/img/0805/0805-upper.jpg")' },
  '0236': { bgSrc: 'url("/img/0236/0236-upper.jpg")' },
  '1524': { bgSrc: 'url("/img/1524/1524-upper.jpg")' },
  '1427': {
    math: {
      'a': { bgSrc: 'url("/img/1427/1427-upper.jpg")' },
      'b': { bgSrc: 'url("/img/1427/1427-upperB.jpg")' },
    }
  },
  '0608': { bgSrc: 'url("/img/0608/0608-upper.jpg")' },
  '1309': { bgSrc: 'url("/img/1309/1309-upper.jpg")' },
  '1405': { bgSrc: 'url("/img/1405/1405-upper.jpg")' },
  '1425': { bgSrc: 'url("/img/1425/1425-upper.jpg")' },
  '1505': { bgSrc: 'url("/img/1505/1505-upper.jpg")' },
  '1626': { bgSrc: 'url("/img/1626/1626-upper.jpg")' },
  '1627': { bgSrc: 'url("/img/1627/1627-upper.jpg")' },  
  '1535': {
    math: {
      'a': { bgSrc: 'url("/img/1535/1535-upperA.jpg")' },
      'b': { bgSrc: 'url("/img/1535/1535-upperB.jpg")' },
    }
  },
};

const bespokeLeftConfig = {
  '0110': {
    default: { bgSrc: 'url("/img/0110/aside-coldseason-layer.jpg")' },
    grs: { bgSrc: 'url("/img/0110/cleanliness-austria-preview.jpg")' },
  },
  '0116': { bgSrc: 'url("/img/0116/aside-hood.jpg")', opacity: 0.45 },
  '2206': { bgSrc: 'url("/img/2206/men-s23_veilance.jpg")' },
  '1001': { bgSrc: 'url("/img/1001/1001-lowerLeft.jpg")' },
  '1029': { bgSrc: 'url("/img/1029/1001-lowerLeft.jpg")' },
  '1027': { bgSrc: 'url("/img/1027/1027-lowerLeft.jpg")' },
  '1028': { bgSrc: 'url("/img/1028/1028-lowerLeft-ski.jpg")' },
  '1030': {
    default: { bgSrc: 'url("/img/1030/1030-lowerLeft-down.jpg")' }, // Overrides 1028 fallback
    treatment: {
      ExpeDRY: { bgSrc: 'url("/img/1030/1030-lowerLeft-discovery.jpg")' }
    }
  },
  '1107': { bgSrc: 'url("/img/1107/aside-two.jpg")' },
  '0324': {
    email: {
      '0324L23FN2790': { bgSrc: 'url("/img/0324/0324-lowerLeft.jpg")' },
      '03240315388': { bgSrc: 'url("/img/0324/0324-lowerLeft-expedry.jpg")' },
    }
  },
  '0805': { bgSrc: 'url("/img/0805/0805-lowerLeft.jpg")' },
  '0236': { bgSrc: 'url("/img/0236/0236-lowerLeft.jpg")' },
  '1524': { bgSrc: 'url("/img/1524/1524-lowerLeft.jpg")' },
  '1427': {
    math: {
      'a': { bgSrc: 'url("/img/1427/1427-lowerLeft.jpg")' },
      'b': { bgSrc: 'url("/img/1427/1427-lowerLeftB.jpg")' },
    }
  },
  '0608': { bgSrc: 'url("/img/0608/0608-lowerLeft.jpg")' },
  '1309': { bgSrc: 'url("/img/1309/1309-lowerLeft.jpg")' },
  '1405': { bgSrc: 'url("/img/1405/1405-lowerLeft.jpg")' },
  '1425': { bgSrc: 'url("/img/1425/1425-lowerLeft.jpg")' },
  '1505': { bgSrc: 'url("/img/1505/1505-lowerLeft.jpg")' },
  '1626': { bgSrc: 'url("/img/1626/1626-lowerLeft.jpg")' },
  '1627': { bgSrc: 'url("/img/1627/1627-lowerLeft.jpg")' },
  '1535': {
    math: {
      'a': { bgSrc: 'url("/img/1535/1535-lowerLeftA.jpg")' },
      'b': { bgSrc: 'url("/img/1535/1535-lowerLeftB.jpg")' },
    }
  },
};

const bespokeRightConfig = {
  '0110': { bgSrc: 'url("/img/0110/aside-two-0110.jpg")', opacity: 0.45 },
  '0116': { bgSrc: 'url("/img/0116/aside-twoB.jpg")', opacity: 0.45 },
  '2206': { bgSrc: 'url("/img/2206/aside-veilance.jpg")' }, // Overrides previous aside-two.jpg
  '1001': { bgSrc: 'url("/img/1001/1001-lowerRight.jpg")' },
  '1029': { bgSrc: 'url("/img/1029/1001-lowerRight.jpg")' },
  '1027': { 
    default: { bgSrc: 'url("/img/1027/1027-lowerRight.jpg")' },
    treatment: {
      ExpeDRY: { bgSrc: 'url("/img/1027/1027-lowerRight-expedry.jpg")' } // This rule was originally an override of 1027 without treatment
    }
  },
  '1028': { bgSrc: 'url("/img/1028/1028-lowerRight-ski.jpg")' },
  '1030': {
    default: { bgSrc: 'url("/img/1028/1028-lowerRight-ski.jpg")' }, // Overridden twice, last one for non-ExpeDRY
    treatment: {
      ExpeDRY: { bgSrc: 'url("/img/1030/1030-lowerRight-discovery.jpg")' }
    }
  },
  '1107': { bgSrc: 'url("/img/1107/aside-three.jpg")' },
  '0324': {
    email: {
      '0324L23FN2790': { bgSrc: 'url("/img/0324/0324-lowerRight.jpg")' },
      '03240315388': { bgSrc: 'url("/img/0324/0324-lowerRight-expedry.jpg")' },
    }
  },
  '0805': { bgSrc: 'url("/img/0805/0805-lowerRight.jpg")' },
  '0236': { bgSrc: 'url("/img/0236/0236-lowerRight.jpg")' },
  '1524': { bgSrc: 'url("/img/1524/1524-lowerRight.jpg")' },
  '1427': {
    math: {
      'a': { bgSrc: 'url("/img/1427/1427-lowerRight.jpg")' },
      'b': { bgSrc: 'url("/img/1427/1427-lowerRightB.jpg")' },
    }
  },
  '0608': { bgSrc: 'url("/img/0608/0608-lowerRight.jpg")' },
  '1309': { bgSrc: 'url("/img/1309/1309-lowerRight.jpg")' },
  '1405': { bgSrc: 'url("/img/1405/1405-lowerRight.jpg")' },
  '1425': { bgSrc: 'url("/img/1425/1425-lowerRight.jpg")' },
  '1505': { bgSrc: 'url("/img/1505/1505-lowerRight.jpg")' },
  '1626': { bgSrc: 'url("/img/1626/1626-lowerRight.jpg")' },
  '1627': { bgSrc: 'url("/img/1627/1627-lowerRight.jpg")' },
  '1535': {
    math: {
      'a': { bgSrc: 'url("/img/1535/1535-lowerRightA.jpg")' },
      'b': { bgSrc: 'url("/img/1535/1535-lowerRightB.jpg")' },
    }
  },
};

export { bespokeUpperConfig, bespokeLeftConfig, bespokeRightConfig };